/**
 * Web-side wrappers around the three milestone-link callables:
 *
 *   - linkPhaseToBacklogMilestone        — append one milestone link
 *   - unlinkPhaseFromBacklogMilestone    — remove one milestone link
 *   - syncBacklogMilestoneNow            — sync one entry or all entries
 *
 * Mirrors the test-runner-injection pattern used by `backlogSearch.ts`
 * so vitest can stub the round-trip without touching `firebase/functions`.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './firebase';

export interface LinkPhaseToBacklogMilestoneBody {
  projectId: string;
  phaseId: string;
  backlogProjectKey: string;
  milestoneId: number;
}

export type LinkPhaseToBacklogMilestoneResult =
  | {
      ok: true;
      milestone: { id: number; name: string; projectKey: string; projectName: string };
      added: string[];
      unchanged: string[];
      totalIssuesInMilestone: number;
      truncated: boolean;
    }
  | {
      ok: false;
      reason:
        | 'config-missing'
        | 'secret-error'
        | 'backlog-error'
        | 'phase-not-found'
        | 'milestone-not-found'
        | 'invalid-project-key';
      message: string;
    };

export interface UnlinkPhaseFromBacklogMilestoneBody {
  projectId: string;
  phaseId: string;
  /** Required: identifies the specific link entry to remove. */
  backlogProjectKey: string;
  milestoneId: number;
  removeImportedItems?: boolean;
}

export type UnlinkPhaseFromBacklogMilestoneResult =
  | { ok: true; cleared: boolean; removedItemCount: number; clearedItemCount: number }
  | {
      ok: false;
      reason: 'phase-not-found' | 'not-linked';
      message: string;
    };

export interface SyncBacklogMilestoneNowBody {
  projectId: string;
  phaseId: string;
  /**
   * Identify a single entry to sync. Omit BOTH to sync every entry on
   * the phase (sequentially, per-entry isolated errors).
   */
  backlogProjectKey?: string;
  milestoneId?: number;
}

export interface PerEntrySyncOutcome {
  backlogProjectKey: string;
  milestoneId: number;
  ok: boolean;
  added: string[];
  removed: string[];
  unchanged: string[];
  totalIssuesInMilestone: number;
  truncated: boolean;
  reason?: 'backlog-error';
  message?: string;
}

export type SyncBacklogMilestoneNowResult =
  | {
      ok: true;
      entries: PerEntrySyncOutcome[];
      added: string[];
      removed: string[];
      unchanged: string[];
      totalIssuesInMilestone: number;
      truncated: boolean;
    }
  | {
      ok: false;
      reason: 'phase-not-found' | 'not-linked' | 'backlog-error';
      message: string;
    };

export type LinkRunner = (
  body: LinkPhaseToBacklogMilestoneBody,
) => Promise<LinkPhaseToBacklogMilestoneResult>;
export type UnlinkRunner = (
  body: UnlinkPhaseFromBacklogMilestoneBody,
) => Promise<UnlinkPhaseFromBacklogMilestoneResult>;
export type SyncNowRunner = (
  body: SyncBacklogMilestoneNowBody,
) => Promise<SyncBacklogMilestoneNowResult>;

let _linkRunner: LinkRunner | null = null;
let _unlinkRunner: UnlinkRunner | null = null;
let _syncRunner: SyncNowRunner | null = null;

export function setLinkRunnerForTests(runner: LinkRunner | null): LinkRunner | null {
  const prev = _linkRunner;
  _linkRunner = runner;
  return prev;
}
export function setUnlinkRunnerForTests(runner: UnlinkRunner | null): UnlinkRunner | null {
  const prev = _unlinkRunner;
  _unlinkRunner = runner;
  return prev;
}
export function setSyncNowRunnerForTests(runner: SyncNowRunner | null): SyncNowRunner | null {
  const prev = _syncRunner;
  _syncRunner = runner;
  return prev;
}

function defaultLinkRunner(): LinkRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      LinkPhaseToBacklogMilestoneBody,
      LinkPhaseToBacklogMilestoneResult
    >(fns, 'linkPhaseToBacklogMilestone');
    const res = await callable(body);
    return res.data;
  };
}

function defaultUnlinkRunner(): UnlinkRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      UnlinkPhaseFromBacklogMilestoneBody,
      UnlinkPhaseFromBacklogMilestoneResult
    >(fns, 'unlinkPhaseFromBacklogMilestone');
    const res = await callable(body);
    return res.data;
  };
}

function defaultSyncNowRunner(): SyncNowRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      SyncBacklogMilestoneNowBody,
      SyncBacklogMilestoneNowResult
    >(fns, 'syncBacklogMilestoneNow');
    const res = await callable(body);
    return res.data;
  };
}

export async function linkPhaseToBacklogMilestone(
  body: LinkPhaseToBacklogMilestoneBody,
): Promise<LinkPhaseToBacklogMilestoneResult> {
  const run = _linkRunner ?? defaultLinkRunner();
  return await run(body);
}

export async function unlinkPhaseFromBacklogMilestone(
  body: UnlinkPhaseFromBacklogMilestoneBody,
): Promise<UnlinkPhaseFromBacklogMilestoneResult> {
  const run = _unlinkRunner ?? defaultUnlinkRunner();
  return await run(body);
}

export async function syncBacklogMilestoneNow(
  body: SyncBacklogMilestoneNowBody,
): Promise<SyncBacklogMilestoneNowResult> {
  const run = _syncRunner ?? defaultSyncNowRunner();
  return await run(body);
}
